import { useEffect, useState } from "react";
import { api } from "../../api";
import { ThumbnailUploader } from "../../components/ThumbnailUploader";
import { RichTextEditor } from "../../components/RichTextEditor";
import {
  SectionHeading,
  StatusBadge,
  ErrorBanner,
  SuccessBanner,
} from "../../components/ui";

interface Row {
  id: string;
  title: string;
  provider: string | null;
  status: string;
  sortOrder: number;
}

type Status = "draft" | "published" | "archived";

const STATUS_LABELS: Record<Status, string> = {
  draft: "下書き",
  published: "公開",
  archived: "アーカイブ",
};
const STATUS_TONE: Record<Status, "success" | "warning" | "danger" | "neutral"> =
  {
    draft: "neutral",
    published: "success",
    archived: "neutral",
  };

interface Form {
  id: string | null;
  title: string;
  provider: string;
  body: string;
  status: Status;
  thumbnailKey: string | null;
  linkUrl: string;
  sortOrder: number;
}

const INITIAL: Form = {
  id: null,
  title: "",
  provider: "",
  body: "",
  status: "published",
  thumbnailKey: null,
  linkUrl: "",
  sortOrder: 0,
};

export function AdminPerksPage() {
  const [items, setItems] = useState<Row[]>([]);
  const [form, setForm] = useState<Form>(INITIAL);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const load = () =>
    api.get<{ items: Row[] }>("/admin/perks").then((r) => setItems(r.items));
  useEffect(() => {
    load();
  }, []);

  const startEdit = async (id: string) => {
    setErr(null);
    setMsg(null);
    try {
      const d = await api.get<{
        id: string;
        title: string;
        provider: string | null;
        body: string | null;
        status: string;
        thumbnailKey: string | null;
        linkUrl: string | null;
        sortOrder: number;
      }>(`/admin/perks/${id}`);
      setForm({
        id: d.id,
        title: d.title,
        provider: d.provider ?? "",
        body: d.body ?? "",
        status: (d.status as Status) ?? "draft",
        thumbnailKey: d.thumbnailKey ?? null,
        linkUrl: d.linkUrl ?? "",
        sortOrder: d.sortOrder ?? 0,
      });
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const cancelEdit = () => {
    setForm(INITIAL);
    setErr(null);
    setMsg(null);
  };

  const submit = async () => {
    setErr(null);
    setMsg(null);
    const payload = {
      title: form.title,
      provider: form.provider || null,
      body: form.body || null,
      status: form.status,
      thumbnailKey: form.thumbnailKey,
      linkUrl: form.linkUrl || null,
      sortOrder: Number(form.sortOrder) || 0,
    };
    try {
      if (form.id) {
        await api.patch(`/admin/perks/${form.id}`, payload);
        setMsg("更新しました");
      } else {
        await api.post("/admin/perks", payload);
        setMsg("作成しました");
      }
      setForm(INITIAL);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const remove = async (id: string) => {
    if (!confirm("この特典を削除します。よろしいですか？")) return;
    setErr(null);
    setMsg(null);
    try {
      await api.delete(`/admin/perks/${id}`);
      setMsg("削除しました");
      if (form.id === id) setForm(INITIAL);
      await load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const isEditing = form.id !== null;

  return (
    <div className="space-y-4">
      <SectionHeading level={2}>会員特典 管理</SectionHeading>
      <div className="card p-4 md:p-5 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-bold text-sm">
            {isEditing ? "編集" : "新規作成"}
          </h3>
          {isEditing && (
            <button
              onClick={cancelEdit}
              className="text-xs underline text-tora-ink/70"
            >
              キャンセル（新規作成に戻る）
            </button>
          )}
        </div>
        <div>
          <label className="label">特典タイトル</label>
          <input
            className="input"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="例: 家事代行 1ヶ月無料"
          />
        </div>
        <div>
          <label className="label">提供者（任意）</label>
          <input
            className="input"
            value={form.provider}
            onChange={(e) => setForm({ ...form, provider: e.target.value })}
            placeholder="例: 茂木社長 / 株式会社〇〇"
          />
        </div>
        <div>
          <label className="label">内容</label>
          <RichTextEditor
            value={form.body}
            onChange={(html) => setForm({ ...form, body: html })}
            placeholder="特典の詳細・利用方法・注意事項など"
          />
        </div>
        <div>
          <label className="label">申込・詳細リンク（任意）</label>
          <input
            className="input"
            value={form.linkUrl}
            onChange={(e) => setForm({ ...form, linkUrl: e.target.value })}
            placeholder="https://..."
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">ステータス</label>
            <select
              className="input"
              value={form.status}
              onChange={(e) =>
                setForm({ ...form, status: e.target.value as Status })
              }
            >
              <option value="draft">下書き</option>
              <option value="published">公開</option>
              <option value="archived">アーカイブ</option>
            </select>
          </div>
          <div>
            <label className="label">表示順（小さいほど上）</label>
            <input
              className="input"
              type="number"
              value={form.sortOrder}
              onChange={(e) =>
                setForm({ ...form, sortOrder: Number(e.target.value) })
              }
            />
          </div>
        </div>
        <ThumbnailUploader
          value={form.thumbnailKey}
          onChange={(k) => setForm({ ...form, thumbnailKey: k })}
        />
        {err && <ErrorBanner>{err}</ErrorBanner>}
        {msg && <SuccessBanner>{msg}</SuccessBanner>}
        <div className="flex flex-wrap items-center gap-2">
          <button className="btn-primary" onClick={submit}>
            {isEditing ? "更新" : "作成"}
          </button>
          {isEditing && form.id && (
            <a
              href={`/perks/${form.id}?preview=1`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-outline text-sm"
            >
              プレビュー（別タブ）
            </a>
          )}
        </div>
        {!isEditing && (
          <p className="text-[11px] text-tora-ink/50">
            プレビューは「下書き」で一度作成・保存すると、編集時に確認できます。
          </p>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-black/10">
          <thead className="bg-black/5 text-left">
            <tr>
              <th className="p-2">表示順</th>
              <th className="p-2">タイトル</th>
              <th className="p-2">提供者</th>
              <th className="p-2">ステータス</th>
              <th className="p-2">操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-black/10">
                <td className="p-2 tabular-nums">{p.sortOrder}</td>
                <td className="p-2">{p.title}</td>
                <td className="p-2 text-xs">{p.provider || "-"}</td>
                <td className="p-2">
                  <StatusBadge tone={STATUS_TONE[p.status as Status] ?? "neutral"}>
                    {STATUS_LABELS[p.status as Status] ?? p.status}
                  </StatusBadge>
                </td>
                <td className="p-2 flex items-center gap-2">
                  <button
                    className="text-tora underline text-xs"
                    onClick={() => startEdit(p.id)}
                  >
                    編集
                  </button>
                  <button
                    className="text-xs text-red-600 hover:underline"
                    onClick={() => remove(p.id)}
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
